import React, { useEffect, useState } from 'react';
import { hasSupabaseConfig, supabase } from '../api/supabaseClient.js';

export default function AuthGate({ children }) {
  const [session, setSession] = useState(undefined);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [mode, setMode] = useState('login');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [preview, setPreview] = useState(false);

  useEffect(() => {
    if (!hasSupabaseConfig) {
      setSession(null);
      return undefined;
    }
    let alive = true;
    supabase.auth.getSession().then(({ data }) => {
      if (alive) setSession(data.session ?? null);
    });
    const { data: subscription } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      if (alive) {
        setSession(nextSession ?? null);
        if (nextSession) setPreview(false);
      }
    });
    return () => {
      alive = false;
      subscription.subscription.unsubscribe();
    };
  }, []);

  async function submit(event) {
    event.preventDefault();
    setMessage('');
    if (!email.trim() || !password) {
      setMessage('Enter both email and password.');
      return;
    }
    if (!hasSupabaseConfig) {
      setMessage('Authentication is not configured in the deployment environment.');
      return;
    }
    setBusy(true);
    try {
      const action = mode === 'login'
        ? supabase.auth.signInWithPassword({ email, password })
        : supabase.auth.signUp({
            email,
            password,
            options: { emailRedirectTo: `${window.location.origin}/` },
          });
      const { error } = await action;
      if (error) throw error;
      if (mode === 'signup') setMessage('Account created. Check your email if confirmation is required.');
    } catch (error) {
      setMessage(error?.message || 'Authentication failed.');
    } finally {
      setBusy(false);
    }
  }

  if (session === undefined && hasSupabaseConfig) {
    return <div className="auth-screen"><div className="auth-card"><div className="brand-mark">SC</div><h1>Stock Critter</h1><p>Connecting secure research workspace…</p></div></div>;
  }

  if (!session && !preview) {
    return <div className="auth-screen">
      <form className="auth-card" onSubmit={submit} noValidate>
        <div className="brand-mark">SC</div>
        <small>RESEARCH INTELLIGENCE</small>
        <h1>Stock Critter</h1>
        <p>Research first. Sign in for private watchlist, portfolio, re-entry history and alerts — or open the product preview now.</p>
        <label>Email<input type="email" value={email} onChange={e=>setEmail(e.target.value)} autoComplete="email" /></label>
        <label>Password
          <div className="auth-password-wrap">
            <input type={showPassword?'text':'password'} value={password} onChange={e=>setPassword(e.target.value)} autoComplete={mode==='login'?'current-password':'new-password'} minLength={6} />
            <button type="button" className="auth-password-toggle" onClick={()=>setShowPassword(v=>!v)} aria-label={showPassword?'Hide password':'Show password'} title={showPassword?'Hide password':'Show password'}>
              {showPassword?'Hide':'Show'}
            </button>
          </div>
        </label>
        {message && <div className="auth-message" role="status">{message}</div>}
        <button className="auth-primary" disabled={busy}>{busy ? 'Working…' : mode === 'login' ? 'Sign in' : 'Create account'}</button>
        <button type="button" className="auth-switch" onClick={()=>{setMode(mode==='login'?'signup':'login');setMessage('');setShowPassword(false)}}>{mode === 'login' ? 'Create an account' : 'Back to sign in'}</button>
        <button type="button" className="auth-switch" onClick={()=>setPreview(true)}>Open product preview</button>
        {!hasSupabaseConfig && <div className="auth-message" role="status">Supabase frontend configuration is missing from the deployment environment. Private account features are unavailable until VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_KEY are configured. The research UI can still be previewed.</div>}
      </form>
    </div>;
  }

  return <>{children}{session
    ? <button className="session-pill" onClick={()=>supabase.auth.signOut()} title={session.user.email || 'Signed in'}>● Signed in · Log out</button>
    : <button className="session-pill" onClick={()=>setPreview(false)} title="Return to sign in">Preview · Sign in</button>}</>;
}
